import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DatePicker, { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { API_URL, customerFetch } from '../utils/api';
import { parisLocalToISO, parisTodayKey, dateToKey, keyToDate } from '../utils/parisTime';
import { useStore } from '../context/StoreContext';
import './CheckoutPage.css';

registerLocale('fr', fr);

const validateName = (name) => {
  if (!name.trim()) return 'Le nom est obligatoire';
  if (name.trim().length < 2) return 'Au moins 2 caractères';
  if (!/^[a-zA-ZÀ-ÿ\s'-]+$/.test(name)) return 'Lettres uniquement';
  return '';
};

const validatePhone = (phone) => {
  const clean = phone.replace(/\s/g, '');
  if (!clean) return 'Le téléphone est obligatoire';
  if (!/^0[1-9]\d{8}$/.test(clean)) return 'Format attendu : 06 12 34 56 78';
  return '';
};

const formatPhone = (value) =>
  value.replace(/\D/g, '').slice(0, 10).match(/.{1,2}/g)?.join(' ') || '';

function CheckoutPage() {
  const navigate = useNavigate();
  const {
    cart, cartTotal, clearCart, customer, loyaltySettings, refreshCustomer
  } = useStore();

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [pickupDate, setPickupDate] = useState(parisTodayKey());
  const [pickupTime, setPickupTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Pré-remplissage depuis le compte fidélité
  useEffect(() => {
    if (customer) {
      setCustomerName(prev => prev || customer.name);
      setCustomerPhone(prev => prev || formatPhone(customer.phone));
    }
  }, [customer]);

  useEffect(() => {
    if (!pickupDate) return;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/available-slots?date=${pickupDate}`);
        const slots = response.ok ? await response.json() : [];
        setAvailableSlots(slots);
        setPickupTime(slots[0] || '');
      } catch {
        setAvailableSlots([]);
      }
    })();
  }, [pickupDate]);

  // Fidélité : les récompenses sont ajoutées depuis l'onglet Fidélité et
  // arrivent dans le panier avec le flag `redeemed`.
  const loyalty = loyaltySettings;
  const loyaltyOn = Boolean(loyalty && loyalty.enabled);
  const welcomePoints = parseInt(loyalty?.welcomePoints) || 0;
  const rewardLines = cart.filter(i => i.redeemed);
  const pointsSpent = rewardLines.reduce((s, i) => s + (i.points_price || 0) * i.quantity, 0);

  // cartTotal (store) = déjà les euros hors récompenses ; réduction = valeur € des articles offerts
  const finalTotal = cartTotal;
  const discount = rewardLines.reduce((s, i) => s + parseFloat(i.price) * i.quantity, 0);
  const pointsToEarn = loyaltyOn && customer
    ? Math.floor(finalTotal) * loyalty.pointsPerEuro
    : 0;

  const validateField = (field, value) => {
    const error = field === 'customerName' ? validateName(value) : validatePhone(value);
    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ customerName: true, customerPhone: true });
    const nameOk = validateField('customerName', customerName);
    const phoneOk = validateField('customerPhone', customerPhone);
    if (!nameOk || !phoneOk || !pickupTime || cart.length === 0) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const response = await customerFetch('/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart.map(item => ({ ...item, payWithPoints: Boolean(item.redeemed) })),
          customerName,
          customerPhone,
          pickupTime: parisLocalToISO(pickupDate, pickupTime)
        })
      });

      if (response.ok) {
        const order = await response.json();
        clearCart();
        refreshCustomer();
        navigate(`/suivi/${order.trackingToken}`, {
          state: { merci: true, pointsToEarn: customer ? pointsToEarn : 0 }
        });
      } else {
        const data = await response.json().catch(() => ({}));
        setSubmitError(data.message || 'Erreur lors de la validation de la commande');
      }
    } catch {
      setSubmitError('Erreur de connexion au serveur');
    } finally {
      setSubmitting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="container-page checkout-vide">
        <span className="checkout-vide-emoji">🍔</span>
        <h1>Votre panier est vide</h1>
        <p>Faites un tour sur la carte pour trouver votre bonheur.</p>
        <Link to="/" className="btn btn-or btn-lg">Voir la carte</Link>
      </div>
    );
  }

  const isFormValid =
    customerName && customerPhone &&
    !errors.customerName && !errors.customerPhone &&
    availableSlots.length > 0;

  return (
    <div className="container-page checkout-page">
      <h1 className="checkout-titre">Finaliser ma commande</h1>

      <div className="checkout-colonnes">
        {/* ---- Récapitulatif ---- */}
        <aside className="card card-pad checkout-recap">
          <h2 className="checkout-recap-titre">Récapitulatif</h2>
          {cart.map((item) => (
            <div key={item.uid} className={`recap-ligne ${item.redeemed ? 'recap-ligne-points' : ''}`}>
              <div className="recap-ligne-haut">
                <span>
                  {item.redeemed && '⭐ '}{item.name} <span className="recap-qty">×{item.quantity}</span>
                </span>
                <span className={item.redeemed ? 'recap-prix-offert' : ''}>
                  {item.redeemed ? 'Offert' : `${(parseFloat(item.price) * item.quantity).toFixed(2)}€`}
                </span>
              </div>
              {item.notes && <div className="recap-notes">💬 {item.notes}</div>}
              {item.redeemed && (
                <div className="recap-ligne-pts-note">{item.points_price * item.quantity} points</div>
              )}
            </div>
          ))}

          {discount > 0 && (
            <div className="recap-ligne recap-reduction">
              <span>⭐ Articles offerts ({pointsSpent} pts)</span>
              <span>−{discount.toFixed(2)}€</span>
            </div>
          )}

          <div className="recap-total">
            <span>À régler sur place</span>
            <strong>{finalTotal.toFixed(2)}€</strong>
          </div>

          {/* ---- Fidélité ---- */}
          {loyaltyOn && customer && (
            <div className="fidelite-box">
              <div className="fidelite-solde">
                <span>⭐ Solde : <strong>{customer.balance - pointsSpent} points</strong></span>
                {pointsToEarn > 0 && (
                  <span className="fidelite-gain">+{pointsToEarn} pts au retrait</span>
                )}
              </div>
              <Link to="/fidelite" className="fidelite-lien">
                {rewardLines.length > 0 ? 'Gérer mes récompenses' : 'Échanger mes points contre un produit'} →
              </Link>
            </div>
          )}

          {loyaltyOn && !customer && (
            <div className="fidelite-box fidelite-invite">
              <p>
                ⭐ Créez un compte fidélité et cumulez des points sur cette commande.
                {welcomePoints > 0 && <strong> {welcomePoints} points offerts à l'inscription !</strong>}
              </p>
              <Link to="/fidelite" className="btn btn-ghost-or btn-sm">Créer mon compte</Link>
            </div>
          )}
        </aside>

        {/* ---- Formulaire ---- */}
        <form className="card card-pad checkout-form" onSubmit={handleSubmit}>
          <div className={`field ${touched.customerName && errors.customerName ? 'has-error' : ''}`}>
            <label htmlFor="nom">Nom</label>
            <input
              id="nom"
              type="text"
              value={customerName}
              placeholder="Votre nom"
              onChange={(e) => {
                setCustomerName(e.target.value);
                if (touched.customerName) validateField('customerName', e.target.value);
              }}
              onBlur={() => { setTouched(t => ({ ...t, customerName: true })); validateField('customerName', customerName); }}
              required
            />
            {touched.customerName && errors.customerName && (
              <p className="erreur">{errors.customerName}</p>
            )}
          </div>

          <div className={`field ${touched.customerPhone && errors.customerPhone ? 'has-error' : ''}`}>
            <label htmlFor="tel">Téléphone</label>
            <input
              id="tel"
              type="tel"
              value={customerPhone}
              placeholder="06 12 34 56 78"
              onChange={(e) => {
                const formatted = formatPhone(e.target.value);
                setCustomerPhone(formatted);
                if (touched.customerPhone) validateField('customerPhone', formatted);
              }}
              onBlur={() => { setTouched(t => ({ ...t, customerPhone: true })); validateField('customerPhone', customerPhone); }}
              required
            />
            {touched.customerPhone && errors.customerPhone && (
              <p className="erreur">{errors.customerPhone}</p>
            )}
          </div>

          <div className="checkout-datetime">
            <div className="field">
              <label>Date de retrait</label>
              <DatePicker
                selected={keyToDate(pickupDate)}
                onChange={(date) => setPickupDate(dateToKey(date))}
                minDate={new Date()}
                dateFormat="dd/MM/yyyy"
                locale="fr"
                className="input"
                required
              />
            </div>

            <div className="field">
              <label>Heure de retrait</label>
              {availableSlots.length > 0 ? (
                <select
                  className="input"
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                  required
                >
                  {availableSlots.map(slot => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
              ) : (
                <p className="msg-info">Aucun créneau disponible ce jour — choisissez une autre date.</p>
              )}
            </div>
          </div>

          <p className="checkout-paiement">
            💳 Paiement sur place : CB, espèces, chèques ou chèques vacances.
          </p>

          {submitError && <div className="msg-erreur">{submitError}</div>}

          <div className="checkout-actions">
            <Link to="/" className="btn btn-ghost">← Retour à la carte</Link>
            <button type="submit" className="btn btn-or btn-lg" disabled={!isFormValid || submitting}>
              {submitting ? 'Envoi…' : `Valider — ${finalTotal.toFixed(2)}€`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CheckoutPage;
